import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { usePoll } from '../hooks/usePoll';

import { Button, Toggle } from '../components/ui';
import { useChrono24Accounts } from '../hooks/marketplaceAccounts';
import { symbolFor } from '../core/currency';
import './chrono24-import.scss';

/**
 * Import a merchant's existing Chrono24 inventory during onboarding.
 *
 * WHY WE ASK FOR A LISTING LINK. This replaced a "your Chrono24 dealer name"
 * field: a dealer name cannot be resolved to the right dealer page, and asking
 * for the dealer page URL fails too because merchants generally cannot find it.
 * A link to one of their own listings is the one thing they always have to
 * hand, and the dealer page is one hop from it. The same link is stored on the
 * marketplace account afterwards, so live publish status starts working without
 * asking twice.
 *
 * THREE STEPS, ONE PANEL
 *   link     — paste a listing link; we resolve the dealer and read the inventory
 *   choose   — every listing with an Import/Skip toggle, plus IMPORT ALL
 *   progress — poll until done, then report what needs a human
 *
 * Decisions are held LOCALLY and sent once when the merchant presses Import.
 * An 800-listing dealer would otherwise fire 800 requests while they flick
 * through the list.
 *
 * Everything created is INACTIVE, and nothing is pushed back to Chrono24 —
 * importing is a read. The copy says so, because "import" could reasonably be
 * read as "publish".
 */

const money = (minor, currency) => {
  if (minor === null || minor === undefined) return 'Price on request';
  return `${symbolFor(currency || 'GBP')}${(minor / 100).toLocaleString(undefined, {
    maximumFractionDigits: 0,
  })}`;
};

// Statuses that mean this item is finished, one way or another.
const TERMINAL = ['created', 'skipped', 'failed', 'needs_review'];

const STATUS_COPY = {
  queued: 'Waiting',
  scraping: 'Reading…',
  created: 'Imported',
  skipped: 'Skipped',
  needs_review: 'Needs a product match',
  failed: 'Could not import',
};

export function Chrono24ImportModal({ storeIdM, onClose, onImported }) {
  const { accounts } = useChrono24Accounts(storeIdM);
  const enabled = useMemo(() => (accounts || []).filter((a) => a.is_enabled), [accounts]);

  const [accountId, setAccountId] = useState(null);
  const [step, setStep] = useState('link');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [headerCount, setHeaderCount] = useState(null);
  const [skipped, setSkipped] = useState(() => new Set());
  const [summary, setSummary] = useState(null);
  // Set once nothing is left in flight: ends the status poll.
  const [importDone, setImportDone] = useState(false);

  // Held in a ref so the polling effect does not tear down and restart its
  // interval every time the parent re-renders with a fresh callback identity.
  const onImportedRef = useRef(onImported);
  onImportedRef.current = onImported;

  const account = useMemo(
    () => enabled.find((a) => a.id === accountId) || null,
    [enabled, accountId]
  );

  // Default to the only account; a store with several (CH24 UK + CH24 Europe)
  // has to choose, because each is a different dealer page with its own stock.
  useEffect(() => {
    if (accountId === null && enabled.length > 0) setAccountId(enabled[0].id);
  }, [enabled, accountId]);

  // If this account already has a resolved dealer, skip straight past the link
  // step — the merchant has already given us what we need.
  useEffect(() => {
    if (account?.ch24_seed_listing_url && !url) setUrl(account.ch24_seed_listing_url);
  }, [account, url]);

  const apiBase = accountId ? `/api/marketplace-accounts/${accountId}/chrono24-import` : null;

  const readError = (err, fallback) =>
    err?.response?.data?.message || err?.response?.data?.reason || err?.message || fallback;

  const loadPreview = async () => {
    if (!apiBase) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(`${apiBase}/preview`, {
        seed_listing_url: url.trim() || null,
      });
      setItems(data.items || []);
      setHeaderCount(data.header_count ?? null);
      // Items already imported on a previous run stay visible but locked, so a
      // second pass cannot duplicate them.
      setSkipped(new Set((data.items || []).filter((i) => i.decision === 'skip').map((i) => i.chrono24_listing_id)));
      setStep('choose');
    } catch (err) {
      setError(readError(err, 'We could not read that Chrono24 listing.'));
    } finally {
      setBusy(false);
    }
  };

  const refreshStatus = useCallback(async () => {
    if (!apiBase) return null;
    const { data } = await axios.get(`${apiBase}/items`);
    setItems(data.items || []);
    setSummary(data.summary || null);
    return data.summary || null;
  }, [apiBase]);

  const startImport = async () => {
    if (!apiBase) return;
    setBusy(true);
    setError(null);
    try {
      // One request: reset everything to import, then mark the merchant's
      // skips. Sending only the skips keeps the payload small for a big dealer.
      const decisions = {};
      skipped.forEach((id) => { decisions[id] = 'skip'; });
      await axios.put(`${apiBase}/decisions`, { import_all: true, decisions });

      await axios.post(`${apiBase}/start`);
      setImportDone(false);
      setStep('progress');
      await refreshStatus();
    } catch (err) {
      setError(readError(err, 'We could not start the import.'));
    } finally {
      setBusy(false);
    }
  };

  // Poll only while work is actually in flight, and always clear on unmount —
  // this panel can be closed mid-import. usePoll (M4.1): a failed poll backs
  // off instead of re-asking every 4 s, 401/403/404 stop it, and a hidden tab
  // asks nothing. Errors surface exactly as before (the global interceptor).
  const pollStatus = useCallback(async () => {
    const next = await refreshStatus();
    if (next && next.in_flight === 0) {
      setImportDone(true);
      onImportedRef.current?.();
    }
  }, [refreshStatus]);

  usePoll(pollStatus, { interval: 4000, enabled: step === 'progress' && !importDone, immediate: true });

  const toggleItem = (chronoId, wantImport) => {
    setSkipped((prev) => {
      const next = new Set(prev);
      if (wantImport) next.delete(chronoId);
      else next.add(chronoId);
      return next;
    });
  };

  const importCount = items.filter(
    (i) => i.status !== 'created' && !skipped.has(i.chrono24_listing_id)
  ).length;

  const needsReview = items.filter((i) => i.status === 'needs_review');
  const failed = items.filter((i) => i.status === 'failed');

  return (
    <div className="rc-ch24imp" role="dialog" aria-modal="true" aria-label="Import from Chrono24">
      <div className="rc-ch24imp__panel">
        <div className="rc-ch24imp__head">
          <div>
            <h2 className="rc-ch24imp__title">Import from Chrono24</h2>
            <p className="rc-ch24imp__sub">
              {step === 'link' && 'We read your public listings — nothing is changed on Chrono24.'}
              {step === 'choose' && (
                <>
                  {items.length} listing{items.length === 1 ? '' : 's'} found
                  {headerCount !== null && headerCount !== items.length
                    ? ` (Chrono24 says ${headerCount} — one may have just sold)`
                    : ''}
                  . Everything imports as an inactive listing for you to review.
                </>
              )}
              {step === 'progress' && 'Importing. You can close this — it keeps going.'}
            </p>
          </div>
          <button type="button" className="rc-ch24imp__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {enabled.length === 0 ? (
          <p className="rc-ch24imp__empty">
            Add a Chrono24 account in Settings ▸ Marketplace Accounts first, then come back here.
          </p>
        ) : (
          <>
            {enabled.length > 1 && (
              <label className="rc-ch24imp__field">
                <span className="rc-ch24imp__label">Which Chrono24 account?</span>
                <select
                  className="rc-ch24imp__input"
                  value={accountId || ''}
                  onChange={(e) => {
                    setAccountId(Number(e.target.value));
                    setStep('link');
                    setItems([]);
                  }}
                >
                  {enabled.map((a) => (
                    <option key={a.id} value={a.id}>{a.label || 'Chrono 24'}</option>
                  ))}
                </select>
              </label>
            )}

            {step === 'link' && (
              <>
                <label className="rc-ch24imp__field">
                  <span className="rc-ch24imp__label">Link to one of your Chrono24 listings</span>
                  <input
                    type="url"
                    className="rc-ch24imp__input"
                    placeholder="https://www.chrono24.co.uk/rolex/…--id47114402.htm"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                  />
                </label>
                <p className="rc-ch24imp__hint">
                  Open any one of your own live listings on Chrono24 and copy the address. We use it
                  to find your dealer page and read the rest of your stock — so you do not have to
                  enter it twice.
                </p>
              </>
            )}

            {step === 'choose' && (
              <>
                <div className="rc-ch24imp__bulk">
                  <span className="rc-ch24imp__count">
                    {importCount} of {items.length} selected
                  </span>
                  <div className="rc-ch24imp__bulkactions">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setSkipped(new Set())}
                    >
                      Import all
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        setSkipped(new Set(items.map((i) => i.chrono24_listing_id)))
                      }
                    >
                      Skip all
                    </Button>
                  </div>
                </div>

                <ul className="rc-ch24imp__list">
                  {items.map((item) => {
                    const done = item.status === 'created';
                    const wantImport = !done && !skipped.has(item.chrono24_listing_id);
                    return (
                      <li key={item.chrono24_listing_id} className="rc-ch24imp__row">
                        {item.image_url ? (
                          <img
                            className="rc-ch24imp__thumb"
                            src={item.image_url}
                            alt=""
                            loading="lazy"
                          />
                        ) : (
                          <span className="rc-ch24imp__thumb rc-ch24imp__thumb--empty" />
                        )}

                        <div className="rc-ch24imp__copy">
                          <span className="rc-ch24imp__name">{item.name || 'Watch'}</span>
                          <span className="rc-ch24imp__meta">{item.title}</span>
                          <span className="rc-ch24imp__meta">
                            {money(item.price_minor, item.currency)}
                            {item.country ? ` · ${item.country}` : ''}
                          </span>
                        </div>

                        {done ? (
                          <span className="rc-ch24imp__status rc-ch24imp__status--done">
                            Already imported
                          </span>
                        ) : (
                          <Toggle
                            bare
                            size="md"
                            checked={wantImport}
                            tone="active"
                            onChange={(next) => toggleItem(item.chrono24_listing_id, next)}
                            title={wantImport ? 'Will be imported' : 'Will be skipped'}
                          />
                        )}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}

            {step === 'progress' && summary && (
              <>
                <div className="rc-ch24imp__progress">
                  <div className="rc-ch24imp__bar">
                    <span
                      className="rc-ch24imp__barfill"
                      style={{
                        width: `${summary.total
                          ? Math.round(
                            ((summary.total - summary.in_flight) / summary.total) * 100
                          )
                          : 0}%`,
                      }}
                    />
                  </div>
                  <span className="rc-ch24imp__count">
                    {summary.by_status.created} imported ·{' '}
                    {summary.in_flight} to go
                    {summary.by_status.skipped ? ` · ${summary.by_status.skipped} skipped` : ''}
                  </span>
                </div>

                {needsReview.length > 0 && (
                  <div className="rc-ch24imp__note">
                    <strong>{needsReview.length}</strong> listing
                    {needsReview.length === 1 ? '' : 's'} could not be matched to a watch in the
                    catalogue, so we have not guessed. They are waiting for you to pick the right
                    one — nothing was created for them.
                  </div>
                )}

                {failed.length > 0 && (
                  <div className="rc-ch24imp__note rc-ch24imp__note--warn">
                    <strong>{failed.length}</strong> could not be read from Chrono24. Run the
                    import again to retry just those.
                  </div>
                )}

                <ul className="rc-ch24imp__list">
                  {items
                    .filter((i) => TERMINAL.includes(i.status) && i.status !== 'skipped')
                    .map((item) => (
                      <li key={item.chrono24_listing_id} className="rc-ch24imp__row">
                        <div className="rc-ch24imp__copy">
                          <span className="rc-ch24imp__name">{item.name || 'Watch'}</span>
                          <span className="rc-ch24imp__meta">{item.title}</span>
                        </div>
                        <span
                          className={`rc-ch24imp__status rc-ch24imp__status--${item.status}`}
                          title={item.error || undefined}
                        >
                          {STATUS_COPY[item.status] || item.status}
                        </span>
                      </li>
                    ))}
                </ul>
              </>
            )}

            {error && <p className="rc-ch24imp__error">{error}</p>}

            <div className="rc-ch24imp__actions">
              {step === 'link' && (
                <Button onClick={loadPreview} disabled={busy || !url.trim()}>
                  {busy ? 'Reading your listings…' : 'Find my listings'}
                </Button>
              )}
              {step === 'choose' && (
                <>
                  <Button variant="secondary" onClick={() => setStep('link')} disabled={busy}>
                    Back
                  </Button>
                  <Button onClick={startImport} disabled={busy || importCount === 0}>
                    {busy
                      ? 'Starting…'
                      : `Import ${importCount} listing${importCount === 1 ? '' : 's'}`}
                  </Button>
                </>
              )}
              {step === 'progress' && (
                <Button variant="secondary" onClick={onClose}>
                  {summary && summary.in_flight === 0 ? 'Done' : 'Close and keep importing'}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Chrono24ImportModal;
